import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for smooth, robust swipe-to-close gesture on mobile (supporting both edges)
 * Handles both fast flicks and slow drags with zero glitching.
 * Uses e.stopPropagation() so stacked panels never interfere with each other.
 * @param {Function} onDismiss - Called when user swipes beyond threshold or flicks
 * @param {boolean} isOpen - Whether the panel is currently active/open
 * @param {boolean} isMobile - Whether device is in mobile viewport
 */
export const useSwipeToClose = (onDismiss, isOpen, isMobile) => {
  const touchStartRef = useRef(null);
  const isHorizontalSwipeRef = useRef(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isSnappingBack, setIsSnappingBack] = useState(false);
  const snapBackTimerRef = useRef(null);
  const lastXRef = useRef(0);
  const lastYRef = useRef(0);

  const handleTouchStart = useCallback((e) => {
    if (!isMobile || !isOpen || !e.touches?.length) return;
    if (e.stopPropagation) e.stopPropagation();

    if (snapBackTimerRef.current) {
      clearTimeout(snapBackTimerRef.current);
      snapBackTimerRef.current = null;
    }
    const touchX = e.touches[0].clientX;
    const touchY = e.touches[0].clientY;
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 360;
    
    // Left-to-right swipe can start anywhere on the left 75% of the screen
    const fromLeftEdge = touchX <= Math.max(160, screenWidth * 0.75);
    // Inward swipe from the right edge
    const fromRightEdge = touchX >= screenWidth - 120;

    if (fromLeftEdge || fromRightEdge) {
      lastXRef.current = touchX;
      lastYRef.current = touchY;
      touchStartRef.current = {
        x: touchX,
        y: touchY,
        time: Date.now(),
        fromLeftEdge,
        fromRightEdge,
        screenWidth,
      };
      isHorizontalSwipeRef.current = false;
      setIsSnappingBack(false);
    }
  }, [isMobile, isOpen]);

  const handleTouchMove = useCallback((e) => {
    if (!touchStartRef.current || !isMobile || !isOpen || !e.touches?.length) return;
    if (e.stopPropagation) e.stopPropagation();
    
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    lastXRef.current = currentX;
    lastYRef.current = currentY;
    const deltaX = currentX - touchStartRef.current.x;
    const deltaY = Math.abs(currentY - touchStartRef.current.y);
    const absDeltaX = Math.abs(deltaX);
    
    // Determine if horizontal swipe intent is established
    if (!isHorizontalSwipeRef.current) {
      // Intent check: horizontal movement in correct direction
      const isRightwardFromLeft = touchStartRef.current.fromLeftEdge && deltaX > 5;
      const isLeftwardFromRight = touchStartRef.current.fromRightEdge && deltaX < -5;

      if ((isRightwardFromLeft || isLeftwardFromRight) && absDeltaX > deltaY * 0.5) {
        isHorizontalSwipeRef.current = true;
        setIsSwiping(true);
      } else if (deltaY > 30 && deltaY > absDeltaX * 1.5) {
        // Obvious vertical scrolling intent, cancel swipe tracking
        touchStartRef.current = null;
        return;
      }
    }

    if (isHorizontalSwipeRef.current) {
      if (e.cancelable) {
        e.preventDefault();
      }
      if (touchStartRef.current.fromLeftEdge) {
        // Left edge: panel physically tracks finger moving to the right
        setSwipeOffset(Math.max(0, deltaX));
      } else {
        // Right edge: inward swipe gesture detected, keep offset at 0 so panel doesn't jump
        setSwipeOffset(0);
      }
    }
  }, [isMobile, isOpen]);

  const handleTouchEnd = useCallback((e) => {
    if (!isOpen) return;
    if (e?.stopPropagation) e.stopPropagation();

    const wasSwiping = isHorizontalSwipeRef.current;
    const startData = touchStartRef.current;
    isHorizontalSwipeRef.current = false;

    if (!startData || !isMobile) {
      touchStartRef.current = null;
      setIsSwiping(false);
      setSwipeOffset(0);
      return;
    }

    const currentX = (e?.changedTouches?.[0]?.clientX) ?? lastXRef.current;
    const currentY = (e?.changedTouches?.[0]?.clientY) ?? lastYRef.current;
    const deltaX = currentX - startData.x;
    const deltaY = Math.abs(currentY - startData.y);
    const elapsed = Math.max(1, Date.now() - startData.time);

    touchStartRef.current = null;
    setIsSwiping(false);

    // Fast flick detection: horizontal movement is dominant
    const isHorizontalDominant = Math.abs(deltaX) > deltaY * 0.5;

    if (startData.fromLeftEdge && deltaX > 0 && isHorizontalDominant) {
      const currentOffset = Math.max(0, deltaX);
      const velocity = currentOffset / elapsed; // px per ms

      // Trigger if: fast flick (velocity > 0.10 or elapsed < 320ms and offset > 15px) OR slow drag > 35px
      const isFastFlick = (velocity > 0.10 && currentOffset > 10) || (elapsed < 320 && currentOffset > 15);
      const isSlowDrag = currentOffset > 35;

      if (isFastFlick || isSlowDrag) {
        onDismiss({ fromSwipe: true, currentOffset });
        return;
      } else {
        setIsSnappingBack(true);
        setSwipeOffset(0);
        snapBackTimerRef.current = setTimeout(() => {
          setIsSnappingBack(false);
          snapBackTimerRef.current = null;
        }, 180);
      }
    } else if (startData.fromRightEdge && deltaX < 0 && isHorizontalDominant) {
      // Swiping inwards from right edge (deltaX < 0)
      const inwardDist = -deltaX;
      const velocity = inwardDist / elapsed;

      // Trigger if: inward flick or drag
      const isFastFlick = (velocity > 0.10 && inwardDist > 10) || (elapsed < 320 && inwardDist > 15);
      const isSlowDrag = inwardDist > 20;

      if (isFastFlick || isSlowDrag) {
        onDismiss({ fromSwipe: true, currentOffset: 0 });
        return;
      } else {
        setSwipeOffset(0);
      }
    } else {
      setSwipeOffset(0);
    }
  }, [isMobile, isOpen, onDismiss]);

  // Reset swipe state when panel closes
  useEffect(() => {
    if (!isOpen) {
      setSwipeOffset(0);
      setIsSwiping(false);
      setIsSnappingBack(false);
      touchStartRef.current = null;
      if (snapBackTimerRef.current) {
        clearTimeout(snapBackTimerRef.current);
        snapBackTimerRef.current = null;
      }
    }
  }, [isOpen]);

  return {
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
      onTouchCancel: handleTouchEnd,
    },
    swipeOffset,
    isSwiping,
    isSnappingBack,
  };
};

export default useSwipeToClose;
